import { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { InputMask } from "primereact/inputmask";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { Calendar } from "primereact/calendar";
import { Toast } from "primereact/toast";
import { useNavigate, useParams } from "react-router-dom";
import { employeeService } from "../../services/EmployeeService";

export default function EmployeeAddEditPage() {
    const [mounted, setMounted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { id } = useParams();
    const isEdit = !!id;

    useEffect(() => { setMounted(true); }, []);

    const toast = useRef(null);
    const navigate = useNavigate();

    const [employee, setEmployee] = useState({
        fullName: "",
        phoneNumber: "",
        email: "",
        address: "",
        iban: "",
        identityNumber: "",
        socialSecurityNumber: "",
        position: "",
        salaryAmount: null,
        salaryPaymentDay: null,
        startDate: null,
        endDate: null,
        note: "",
        isActive: true
    });

    useEffect(() => {
        if (isEdit) {
            const fetchEmployee = async () => {
                try {
                    const res = await employeeService.getById(id);
                    if (res.data.success) {
                        const data = res.data.data;
                        // Convert dates
                        if (data.startDate) data.startDate = new Date(data.startDate);
                        if (data.endDate) data.endDate = new Date(data.endDate);
                        setEmployee(data);
                    }
                } catch (err) {
                    console.error("Çalışan bilgileri alınamadı", err);
                    toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Çalışan bilgileri yüklenemedi' });
                }
            };
            fetchEmployee();
        }
    }, [id, isEdit]);

    const handleChange = (field, value) => {
        setEmployee(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!employee.fullName) {
            toast.current.show({
                severity: 'warn',
                summary: 'Eksik Bilgi',
                detail: 'Ad Soyad alanı zorunludur.',
                life: 3000
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let response;
            if (isEdit) {
                response = await employeeService.update(employee);
            } else {
                response = await employeeService.create(employee);
            }

            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Başarılı',
                    detail: isEdit ? 'Çalışan başarıyla güncellendi' : 'Çalışan başarıyla kaydedildi',
                    life: 3000
                });
                setTimeout(() => navigate(`/employees`), 1500);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Hata',
                    detail: response.data.message || 'Kaydedilirken bir hata oluştu',
                    life: 3000
                });
            }
        } catch (err) {
            console.error(err);
            toast.current.show({
                severity: 'error',
                summary: 'Hata',
                detail: 'Kaydedilirken bir hata oluştu',
                life: 3000
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-1">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
            <Card>
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div>
                        <h2 className="m-0 text-gray-900 font-bold text-2xl flex items-center gap-2">
                            <i className={`pi ${isEdit ? 'pi-user-edit' : 'pi-user-plus'} text-indigo-600 !text-[1.8rem]`}></i>
                            {isEdit ? 'Çalışan Düzenle' : 'Yeni Çalışan Ekle'}
                        </h2>
                        <p className="text-gray-600 m-0 mt-1">Sisteme çalışan kaydı tanımlayın.</p>
                    </div>
                    <div className="flex gap-2">
                        <Button label="Geri Dön" icon="pi pi-arrow-left" className="p-button-outlined p-button-secondary rounded-lg" onClick={() => navigate('/employees')} disabled={isSubmitting} />
                        <Button label={isEdit ? "Güncelle" : "Kaydet"} icon="pi pi-save" className="p-button-success rounded-lg px-4" onClick={handleSave} loading={isSubmitting} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8">
                        <Card title={<div className="flex items-center gap-2 text-indigo-600 uppercase text-sm font-bold"><i className="pi pi-id-card"></i><span>Kişisel Bilgiler</span></div>} className="shadow-md rounded-xl mb-6 border-t-4 border-indigo-600">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 p-fluid">
                                <div className="md:col-span-2">
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Ad Soyad</label>
                                    <InputText value={employee.fullName} onChange={(e) => handleChange("fullName", e.target.value)} placeholder="Örn: Ahmet Yılmaz" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Telefon</label>
                                    <InputMask value={employee.phoneNumber} onChange={(e) => handleChange("phoneNumber", e.target.value)} mask="0(999) 999 99 99" placeholder="0(5xx) xxx xx xx" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">E-Mail</label>
                                    <InputText value={employee.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="ornek@mail.com" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">T.C. Kimlik No</label>
                                    <InputText value={employee.identityNumber} onChange={(e) => handleChange("identityNumber", e.target.value)} maxLength={11} />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">SGK No</label>
                                    <InputText value={employee.socialSecurityNumber} onChange={(e) => handleChange("socialSecurityNumber", e.target.value)} />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Adres</label>
                                    <InputTextarea rows={2} value={employee.address} onChange={(e) => handleChange("address", e.target.value)} autoResize />
                                </div>
                            </div>
                        </Card>

                        <Card title={<div className="flex items-center gap-2 text-indigo-600 uppercase text-sm font-bold"><i className="pi pi-briefcase"></i><span>İş Bilgileri</span></div>} className="shadow-md rounded-xl mb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3 p-fluid">
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Pozisyon / Görev</label>
                                    <InputText value={employee.position} onChange={(e) => handleChange("position", e.target.value)} placeholder="Örn: Satış Temsilcisi" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Maaş Tutarı</label>
                                    <InputNumber value={employee.salaryAmount} onValueChange={(e) => handleChange("salaryAmount", e.value)} mode="currency" currency="TRY" locale="tr-TR" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Maaş Ödeme Günü</label>
                                    <InputNumber value={employee.salaryPaymentDay} onValueChange={(e) => handleChange("salaryPaymentDay", e.value)} min={1} max={31} placeholder="Örn: 1" />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">İşe Başlama Tarihi</label>
                                    <Calendar value={employee.startDate} onChange={(e) => handleChange("startDate", e.value)} dateFormat="dd.mm.yy" showIcon appendTo={mounted ? document.body : "self"} />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">İşten Ayrılış Tarihi</label>
                                    <Calendar value={employee.endDate} onChange={(e) => handleChange("endDate", e.value)} dateFormat="dd.mm.yy" showIcon appendTo={mounted ? document.body : "self"} />
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="lg:col-span-4">
                        <Card title={<div className="flex items-center gap-2 text-indigo-600 uppercase text-sm font-bold"><i className="pi pi-info-circle"></i><span>Diğer Bilgiler</span></div>} className="shadow-md rounded-xl border-t-4 border-indigo-600">
                            <div className="flex flex-col gap-4 p-fluid">
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">IBAN</label>
                                    <InputText value={employee.iban} onChange={(e) => handleChange("iban", e.target.value)} placeholder="TR00 ..." />
                                </div>
                                <div>
                                    <label className="font-bold text-gray-900 mb-2 block text-sm">Not</label>
                                    <InputTextarea rows={4} value={employee.note} onChange={(e) => handleChange("note", e.target.value)} autoResize />
                                </div>
                                {isEdit && (
                                    <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg border border-gray-200">
                                        <Checkbox inputId="isActive" checked={employee.isActive} onChange={(e) => handleChange("isActive", e.checked)} />
                                        <label htmlFor="isActive" className="text-sm font-bold text-gray-700 cursor-pointer">Aktif Çalışan</label>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </Card>
        </div>
    );
}
